import React, { useState } from 'react';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faTrash, faClock, faUndo } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';

const CellDetailPanel = ({ cell, dimensions, onClose, onUpdate, onDelete, onCancelPendingDeletion, isLocked = false, pendingAssignments = [], pendingModifications = [], assignmentStatus = null }) => {
  const [targetCount, setTargetCount] = useState(cell.target_count || 0);
  const [notes, setNotes] = useState(cell.notes || '');
  const [dimensionValues, setDimensionValues] = useState(cell.dimension_values_json || {});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });

  const handleSave = async () => {
    const newTarget = parseInt(targetCount);
    const completes = cell.current_count || 0;

    if (newTarget < completes) {
      setAlertModal({
        show: true,
        title: 'Cible invalide',
        message: `La cible ne peut pas être inférieure au nombre déjà complété (${completes}).`,
        type: 'warning'
      });
      return;
    }

    try {
      setSaving(true);
      const payload = {};

      // Only include target_count when it actually changed
      if (newTarget !== (cell.target_count || 0)) {
        payload.target_count = newTarget;
      }

      // Only include notes when actually changed
      if (notes !== (cell.notes || '')) {
        payload.notes = notes;
      }

      // Include dimension values if changed
      const originalDimValues = cell.dimension_values_json || {};
      const dimValuesChanged = JSON.stringify(dimensionValues) !== JSON.stringify(originalDimValues);
      if (dimValuesChanged) {
        payload.dimension_values_json = dimensionValues;
      }

      // Don't send if nothing changed
      if (Object.keys(payload).length === 0) {
        setSaving(false);
        onClose();
        return;
      }

      const response = await axios.put(
        `/quotas/projects/${cell.quota_project_id}/cells/${cell.id}`,
        payload
      );
      onUpdate(response.data.data);
      // If any pending modification was created, show info message instead of closing
      const hasPending = response.data.data?.pending_target_count !== undefined
        || response.data.data?.pending_notes !== undefined
        || response.data.data?.pending_dimension_values_json !== undefined;
      if (hasPending) {
        setAlertModal({
          show: true,
          title: 'Modification enregistrée',
          message: response.data.message || 'La modification a été enregistrée et sera soumise pour approbation.',
          type: 'info'
        });
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error updating cell:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la sauvegarde',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (cell.current_count > 0) {
      setAlertModal({
        show: true,
        title: 'Suppression impossible',
        message: `Cette cellule a déjà ${cell.current_count} quota(s) complété(s). Les cellules avec des quotas complétés ne peuvent pas être supprimées.`,
        type: 'warning'
      });
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await axios.delete(`/quotas/projects/${cell.quota_project_id}/cells/${cell.id}`);

      if (response.data.pending_deletion) {
        // chef_terrain: pending deletion created
        onUpdate({
          ...response.data.data,
          _pendingDeletion: true,
        });
        setShowDeleteConfirm(false);
        setAlertModal({
          show: true,
          title: 'En attente d\'approbation',
          message: response.data.message,
          type: 'info'
        });
      } else {
        onDelete(cell.id);
        onClose();
      }
    } catch (error) {
      console.error('Error deleting cell:', error);
      setShowDeleteConfirm(false);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de la suppression',
        type: 'error'
      });
    }
  };

  const getDimensionValueLabel = (dimensionId, valueCode) => {
    const dimension = dimensions.find(d => d.id === dimensionId);
    if (!dimension || !dimension.values_json) return valueCode;
    const value = dimension.values_json.find(v => v.code === valueCode);
    return value?.label || valueCode;
  };

  const hasPendingDeletion = pendingModifications.some(
    pm => Number(pm.quota_cell_id) === Number(cell.id) && pm.field === 'delete'
  );

  const hasPendingCreation = pendingModifications.some(
    pm => Number(pm.quota_cell_id) === Number(cell.id) && pm.field === 'create'
  );

  return (
    <div className="cell-detail-panel">
      <div className="panel-header">
        <h3>Détails de la Cellule</h3>
        <button className="btn-icon" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="panel-content">
        {hasPendingCreation && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-700 text-sm">
            <FontAwesomeIcon icon={faClock} />
            <span className="font-medium">Cette cellule est en attente d'approbation (ajout)</span>
          </div>
        )}

        {hasPendingDeletion && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-700 text-sm">
              <FontAwesomeIcon icon={faClock} />
              <span className="font-medium">Cette cellule est en attente de suppression</span>
            </div>
            {assignmentStatus !== 'pending_approval' && onCancelPendingDeletion && (
              <button
                className="text-xs text-red-600 hover:text-red-800 underline"
                onClick={() => onCancelPendingDeletion(cell.id)}
              >
                Annuler
              </button>
            )}
          </div>
        )}

        <div className="detail-section">
          <label>Code de la cellule</label>
          <div className="detail-value">{cell.cell_code}</div>
        </div>

        <div className="detail-section">
          <label>Combinaison</label>
          <div className="dimension-values">
            {dimensions.map(dim => {
              const valueCode = dimensionValues[dim.id];
              if (isLocked) {
                // Read-only when locked
                if (!valueCode) return null;
                return (
                  <div key={dim.id} className="dimension-value-item">
                    <span className="dim-label">{dim.label}:</span>
                    <span className="dim-value">{getDimensionValueLabel(dim.id, valueCode)}</span>
                  </div>
                );
              }
              // Editable dropdown
              const values = dim.values_json || [];
              return (
                <div key={dim.id} className="dimension-value-item" style={{ marginBottom: '8px' }}>
                  <span className="dim-label" style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>{dim.label}:</span>
                  <select
                    value={valueCode || ''}
                    onChange={(e) => {
                      setDimensionValues(prev => ({
                        ...prev,
                        [dim.id]: e.target.value
                      }));
                    }}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
                  >
                    {values.map(v => (
                      <option key={v.code} value={v.code}>{v.label || v.code}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
          {/* Pending dimension modification indicator */}
          {(() => {
            const cellId = Number(cell.id);
            const pendingDimMod = pendingModifications.find(
              pm => Number(pm.quota_cell_id) === cellId && pm.field === 'dimension_values_json'
            );
            if (pendingDimMod) {
              const oldDimValues = pendingDimMod.old_value_text ? JSON.parse(pendingDimMod.old_value_text) : null;
              const newDimValues = pendingDimMod.new_value_text ? JSON.parse(pendingDimMod.new_value_text) : null;
              const formatCombo = (dimValues) => dimensions.map(dim => {
                const code = dimValues?.[dim.id];
                return code ? getDimensionValueLabel(dim.id, code) : null;
              }).filter(Boolean).join(' - ');
              return (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                  <div className="flex items-center gap-2 text-yellow-800 text-sm">
                    <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                    <span className="font-medium">Modification de combinaison en attente</span>
                  </div>
                  <div className="text-xs text-yellow-700 mt-1">
                    Combinaison actuelle: <strong>{formatCombo(oldDimValues)}</strong> → Nouvelle combinaison: <strong>{formatCombo(newDimValues)}</strong>
                  </div>
                  {pendingDimMod.requested_by?.name && (
                    <div className="text-xs text-gray-500 mt-1">
                      Demandé par {pendingDimMod.requested_by.name}
                    </div>
                  )}
                  {pendingDimMod.requested_at && (
                    <div className="text-xs text-gray-500">
                      le {new Date(pendingDimMod.requested_at).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })()}
        </div>

        <div className="detail-section">
          <label>Cible</label>
          <input
            type="number"
            value={targetCount}
            onChange={(e) => setTargetCount(e.target.value)}
            min={cell.current_count || 0}
            disabled={isLocked}
          />
          {parseInt(targetCount) < (cell.current_count || 0) && (
            <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
              La cible ne peut pas être inférieure au complété ({cell.current_count})
            </p>
          )}
          {/* Pending target modification indicator */}
          {(() => {
            const cellId = Number(cell.id);
            const pendingTargetMod = pendingModifications.find(
              pm => Number(pm.quota_cell_id) === cellId && pm.field === 'target_count'
            );
            if (pendingTargetMod) {
              return (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                  <div className="flex items-center gap-2 text-yellow-800 text-sm">
                    <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                    <span className="font-medium">Modification en attente d'approbation</span>
                  </div>
                  <div className="text-xs text-yellow-700 mt-1">
                    Cible actuelle: <strong>{pendingTargetMod.old_value}</strong> → Nouvelle cible: <strong>{pendingTargetMod.new_value}</strong>
                  </div>
                  {pendingTargetMod.requested_by?.name && (
                    <div className="text-xs text-gray-500 mt-1">
                      Demandé par {pendingTargetMod.requested_by.name}
                    </div>
                  )}
                  {pendingTargetMod.requested_at && (
                    <div className="text-xs text-gray-500">
                      le {new Date(pendingTargetMod.requested_at).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })()}
        </div>

        <div className="detail-section">
          <label>Actuel</label>
          <div className="detail-value">{cell.current_count || 0}</div>
        </div>

        <div className="detail-section">
          <label>Restant</label>
          <div className="detail-value">
            {Math.max(0, (cell.target_count || 0) - (cell.current_count || 0))}
          </div>
        </div>

        <div className="detail-section">
          <label>% Alloué</label>
          <div className="progress-display">
            <div className="progress-bar-large">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${cell.target_count > 0 
                    ? Math.min(100, ((cell.current_count || 0) / cell.target_count) * 100) 
                    : 0}%` 
                }}
              />
            </div>
            <span className="progress-percentage">
              {cell.target_count > 0 
                ? (((cell.current_count || 0) / cell.target_count) * 100).toFixed(1)
                : 0}%
            </span>
          </div>
        </div>

        {/* Approved supervisors section */}
        {(() => {
          // Get approved supervisors (from new system or legacy)
          const approvedSupervisors = cell.assigned_supervisors || [];
          
          if (approvedSupervisors.length > 0) {
            return (
              <div className="detail-section">
                <label>Prestataire{approvedSupervisors.length > 1 ? 's' : ''} assigné{approvedSupervisors.length > 1 ? 's' : ''}</label>
                <div className="detail-value">
                  <div className="space-y-2">
                    {approvedSupervisors.map((supervisor, idx) => {
                      const assignedCount = supervisor.assigned_count;
                      const completes = supervisor.completes || 0;
                      return (
                        <div key={idx} className="supervisor-info bg-green-50 border border-green-200 rounded p-3 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="supervisor-name font-medium text-green-800 break-words">{supervisor.name || 'N/A'}</div>
                              {supervisor.ref && (
                                <div className="supervisor-ref text-sm text-green-600 mt-0.5">({supervisor.ref})</div>
                              )}
                            </div>
                            {assignedCount !== null && assignedCount !== undefined && (
                              <span className="text-sm font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded whitespace-nowrap flex-shrink-0">
                                {completes}/{assignedCount} quota{assignedCount > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          } else {
            return (
              <div className="detail-section">
                <label>Prestataire assigné</label>
                <div className="detail-value">
                  <span className="no-supervisor">Aucun prestataire assigné</span>
                </div>
              </div>
            );
          }
        })()}

        {/* Pending assignments section */}
        {(() => {
          const cellId = Number(cell.id);
          const cellPendingAssignments = pendingAssignments.filter(pa => Number(pa.quota_cell_id) === cellId);
          
          if (cellPendingAssignments.length > 0) {
            return (
              <div className="detail-section">
                <label className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                  Prestataire{cellPendingAssignments.length > 1 ? 's' : ''} non approuvé{cellPendingAssignments.length > 1 ? 's' : ''}
                </label>
                <div className="detail-value">
                  <div className="space-y-2">
                    {cellPendingAssignments.map((pendingAssignment, idx) => {
                      const supervisorName = pendingAssignment.supervisor?.name || pendingAssignment.assigned_supervisor?.name || pendingAssignment.assignedSupervisor?.name || 'N/A';
                      const supervisorRef = pendingAssignment.supervisor?.ref || pendingAssignment.assigned_supervisor?.ref || pendingAssignment.assignedSupervisor?.ref;
                      const assignedCount = pendingAssignment.assigned_count;
                      const completes = pendingAssignment.completes || 0;
                      return (
                        <div key={idx} className="supervisor-info bg-yellow-50 border border-yellow-200 rounded p-3 min-w-0">
                          <div className="flex flex-col gap-2">
                            {/* First row: Supervisor name and assigned count */}
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="supervisor-name font-medium text-yellow-800 break-words">
                                  {supervisorName}
                                </div>
                                {supervisorRef && (
                                  <div className="supervisor-ref text-sm text-yellow-600 mt-0.5">
                                    ({supervisorRef})
                                  </div>
                                )}
                              </div>
                              {assignedCount !== null && assignedCount !== undefined && (
                                <span className="text-sm font-semibold text-yellow-700 bg-yellow-100 px-2.5 py-1 rounded whitespace-nowrap flex-shrink-0">
                                  {completes}/{assignedCount} quota{assignedCount > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            {/* Second row: Assignment date */}
                            {pendingAssignment.assigned_at && (
                              <div className="text-xs text-gray-500">
                                Assigné le {new Date(pendingAssignment.assigned_at).toLocaleDateString('fr-FR', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            )}
                            {/* Third row: Assigner name */}
                            {pendingAssignment.assigned_by?.name && (
                              <div className="text-xs text-gray-500">
                                par {pendingAssignment.assigned_by.name}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        <div className="detail-section">
          <label>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="4"
            placeholder="Ajouter des notes..."
            disabled={isLocked}
          />
          {/* Pending notes modification indicator */}
          {(() => {
            const cellId = Number(cell.id);
            const pendingNotesMod = pendingModifications.find(
              pm => Number(pm.quota_cell_id) === cellId && pm.field === 'notes'
            );
            if (pendingNotesMod) {
              return (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                  <div className="flex items-center gap-2 text-yellow-800 text-sm">
                    <FontAwesomeIcon icon={faClock} className="text-yellow-600" />
                    <span className="font-medium">Modification des notes en attente</span>
                  </div>
                  {pendingNotesMod.new_value_text && (
                    <div className="text-xs text-yellow-700 mt-1">
                      Nouvelles notes: <em>{pendingNotesMod.new_value_text.length > 100 ? pendingNotesMod.new_value_text.substring(0, 100) + '...' : pendingNotesMod.new_value_text}</em>
                    </div>
                  )}
                  {pendingNotesMod.requested_by?.name && (
                    <div className="text-xs text-gray-500 mt-1">
                      Demandé par {pendingNotesMod.requested_by.name}
                    </div>
                  )}
                  {pendingNotesMod.requested_at && (
                    <div className="text-xs text-gray-500">
                      le {new Date(pendingNotesMod.requested_at).toLocaleDateString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>

      {!isLocked && (
        <div className="panel-footer">
          {hasPendingDeletion ? (
            <button
              className="btn-warning"
              onClick={() => onCancelPendingDeletion && onCancelPendingDeletion(cell.id)}
              disabled={assignmentStatus === 'pending_approval'}
              title="Annuler la suppression en attente"
            >
              <FontAwesomeIcon icon={faUndo} />
              Annuler la suppression
            </button>
          ) : (
            <button
              className={`btn-danger ${cell.current_count > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleDelete}
              disabled={cell.current_count > 0}
              title={cell.current_count > 0 ? `Cette cellule a ${cell.current_count} quota(s) complété(s) et ne peut pas être supprimée` : 'Supprimer la cellule'}
            >
              <FontAwesomeIcon icon={faTrash} />
              Supprimer
            </button>
          )}
          {!hasPendingDeletion && (
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving}
              title="Sauvegarder les modifications"
            >
              <FontAwesomeIcon icon={faSave} />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          )}
        </div>
      )}

      <ConfirmationModal
        show={showDeleteConfirm}
        title="Supprimer la cellule"
        message="Êtes-vous sûr de vouloir supprimer cette cellule ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default CellDetailPanel;

